import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import {
  OBSERVATION_DISPOSITION_WORKFLOW_HINT,
  OBSERVATION_DISPOSITION_WORKFLOW_TITLE,
} from '../../domain/incidents/observationDispositionCopy';

function displayJourneyPhaseTitle(
  phaseId: string,
  fallbackTitle: string,
  observationDispositionMode: boolean,
): string {
  switch (phaseId) {
    case 'detect':
      return 'Detected';
    case 'verify':
      return 'Review';
    case 'decide':
      return observationDispositionMode ? fallbackTitle : 'Assign Action';
    case 'follow_up':
      return 'Impact Measurement';
    case 'closeout':
      return 'Report';
    default:
      return fallbackTitle;
  }
}

export type IncidentWorkflowRailProps = {
  railRef: React.RefObject<HTMLDivElement>;
  isPageMode: boolean;
  observationDispositionMode?: boolean;
  incidentId?: string;
  onOpenReviewPage?: (incidentId: string) => void;
  journeyPhases: Array<{ id: string; title: string }>;
  activeJourneyPhase: string;
  journeyPhaseMeta: (phaseId: any) => { label: string; color: string; buttonColor: 'inherit' | 'primary' | 'success' | 'warning' };
  scrollToJourneyPhase: (phaseId: any) => void;
  nextIncompleteJourneyStep: string;
  activeJourneyStep: string;
  journeySteps: Array<{ id: string; title: string }>;
};

const IncidentWorkflowRail: React.FC<IncidentWorkflowRailProps> = ({
  railRef,
  isPageMode,
  observationDispositionMode = false,
  incidentId,
  onOpenReviewPage,
  journeyPhases,
  activeJourneyPhase,
  journeyPhaseMeta,
  scrollToJourneyPhase,
  nextIncompleteJourneyStep,
  activeJourneyStep,
  journeySteps,
}) => (
  <Paper
    ref={railRef}
    variant="outlined"
    data-testid="incident-flow-rail"
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 3,
      p: 1.1,
      mb: 1.5,
      borderRadius: 3,
      borderColor: '#d0d7e2',
      bgcolor: 'rgba(255, 255, 255, 0.94)',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
    }}
  >
    <Typography sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}>
      {observationDispositionMode ? OBSERVATION_DISPOSITION_WORKFLOW_TITLE : 'Safety workflow'}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.85, lineHeight: 1.45 }}>
      {observationDispositionMode
        ? OBSERVATION_DISPOSITION_WORKFLOW_HINT
        : isPageMode
          ? 'Use the phase buttons below to move through Detected, Review, Assign Action, Impact Measurement, and Report.'
          : 'Follow the Detected, Review, Assign Action, Impact Measurement, and Report flow without losing the incident trail.'}
    </Typography>
    {!isPageMode && incidentId && typeof onOpenReviewPage === 'function' && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
        <Button size="small" variant="outlined" onClick={() => onOpenReviewPage(incidentId)}>
          Open full review page
        </Button>
      </Box>
    )}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {journeyPhases.map((phase) => (
        <Box key={phase.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Button
            size="small"
            variant={activeJourneyPhase === phase.id ? 'contained' : 'outlined'}
            color={journeyPhaseMeta(phase.id).buttonColor}
            onClick={() => scrollToJourneyPhase(phase.id)}
            sx={{ minWidth: 0, px: 1.1, borderRadius: 999, fontWeight: 800, textTransform: 'none' }}
          >
            {displayJourneyPhaseTitle(String(phase.id), phase.title, observationDispositionMode)}
          </Button>
          <Typography
            variant="caption"
            data-testid={`incident-flow-state-${phase.id}`}
            sx={{ fontWeight: 800, color: journeyPhaseMeta(phase.id).color }}
          >
            {journeyPhaseMeta(phase.id).label}
          </Typography>
        </Box>
      ))}
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
      In view: {displayJourneyPhaseTitle(
        String(activeJourneyPhase),
        journeyPhases.find((phase) => phase.id === activeJourneyPhase)?.title || 'Review',
        observationDispositionMode,
      )} · {journeySteps.find((step) => step.id === activeJourneyStep)?.title || 'Incident flow'}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
      Up next: {journeySteps.find((step) => step.id === nextIncompleteJourneyStep)?.title || 'Confirm review record'}
    </Typography>
  </Paper>
);

export default IncidentWorkflowRail;
